import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Camera, X, Trash2, RotateCcw, Save, FileText, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import { logger } from '../../utils/logger';

/**
 * CameraScanner — full-screen document scanner modal.
 *
 * Opens the device camera (rear-facing when available), lets the user
 * capture multiple pages, reorder/delete, then bundles all captured
 * pages into a single PDF File and returns it via `onCapture(file)`.
 *
 * Props:
 *   open:        boolean — controls visibility
 *   onClose:     () => void — dismiss without saving
 *   onCapture:   (file: File) => void | Promise<void> — invoked with the finished PDF
 *   defaultName: optional base filename (default: "scan")
 */
export const CameraScanner = ({ open, onClose, onCapture, defaultName = 'scan' }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [pages, setPages] = useState([]); // [{ id, dataUrl, width, height }]
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(null); // null = live view

  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera access is not supported on this device or browser.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      logger.error('Camera start failed', e);
      const name = e?.name || '';
      if (name === 'NotAllowedError') setError('Camera permission was denied. Please grant access and try again.');
      else if (name === 'NotFoundError') setError('No camera found on this device.');
      else setError(e?.message || 'Could not start camera.');
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    startStream();
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cleanup on unmount
  useEffect(() => () => stopStream(), [stopStream]);

  if (!open) return null;

  const capturePage = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    const canvas = document.createElement('canvas');
    const vw = video.videoWidth || 1280;
    const vh = video.videoHeight || 960;
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, vw, vh);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    setPages((prev) => [
      ...prev,
      { id: `${Date.now()}-${prev.length}`, dataUrl, width: vw, height: vh },
    ]);
  };

  const deletePage = (id) => {
    setPages((prev) => prev.filter((p) => p.id !== id));
    setReviewIndex(null);
  };

  const retakePage = (id) => {
    setPages((prev) => prev.filter((p) => p.id !== id));
    setReviewIndex(null);
  };

  const handleCancel = () => {
    stopStream();
    setPages([]);
    setReviewIndex(null);
    onClose?.();
  };

  const buildPdf = async () => {
    if (!pages.length) return;
    setSaving(true);
    try {
      // Standard A4 portrait (pt units: 595 × 842).
      const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 24;

      pages.forEach((p, idx) => {
        if (idx > 0) pdf.addPage();
        // Fit image to page while preserving aspect ratio
        const iw = p.width, ih = p.height;
        const availW = pageW - margin * 2;
        const availH = pageH - margin * 2;
        const scale = Math.min(availW / iw, availH / ih);
        const w = iw * scale;
        const h = ih * scale;
        const x = (pageW - w) / 2;
        const y = (pageH - h) / 2;
        pdf.addImage(p.dataUrl, 'JPEG', x, y, w, h, undefined, 'FAST');
      });

      const blob = pdf.output('blob');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const file = new File([blob], `${defaultName}-${ts}.pdf`, { type: 'application/pdf' });
      await onCapture?.(file);
      stopStream();
      setPages([]);
      setReviewIndex(null);
    } catch (err) {
      logger.error('PDF build failed', err);
      alert('Failed to build PDF. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const reviewing = reviewIndex != null && pages[reviewIndex];

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="camera-scanner">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white bg-black/40" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}>
        <button
          onClick={handleCancel}
          data-testid="scanner-close"
          className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-95"
          aria-label="Cancel scan"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-sm font-semibold">
          {reviewing ? `Page ${reviewIndex + 1} of ${pages.length}` : `Scan Document${pages.length ? ` · ${pages.length} page${pages.length === 1 ? '' : 's'}` : ''}`}
        </div>
        <button
          onClick={buildPdf}
          disabled={!pages.length || saving}
          data-testid="scanner-save"
          className="px-3 py-1.5 rounded-lg bg-teal-500 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-1 active:scale-95"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{saving ? 'Building…' : 'Save PDF'}</span>
        </button>
      </div>

      {/* Main viewer */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center">
        {error ? (
          <div className="text-center text-white px-6" data-testid="scanner-error">
            <Camera className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="font-semibold mb-1">Camera unavailable</p>
            <p className="text-sm text-white/70 mb-4">{error}</p>
            <button
              onClick={startStream}
              className="px-4 py-2 bg-white/10 rounded-lg text-sm font-medium"
            >
              Try again
            </button>
          </div>
        ) : reviewing ? (
          <img
            src={reviewing.dataUrl}
            alt={`Page ${reviewIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              className="w-full h-full object-cover"
              data-testid="scanner-video"
            />
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
            {/* Document framing overlay */}
            {!starting && (
              <div
                className="absolute pointer-events-none border-2 border-white/60 rounded-xl"
                style={{ inset: '10% 6%' }}
                aria-hidden
              />
            )}
          </>
        )}

        {/* Review nav arrows */}
        {reviewing && pages.length > 1 && (
          <>
            <button
              onClick={() => setReviewIndex((i) => Math.max(0, i - 1))}
              disabled={reviewIndex === 0}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center disabled:opacity-30"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setReviewIndex((i) => Math.min(pages.length - 1, i + 1))}
              disabled={reviewIndex === pages.length - 1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center disabled:opacity-30"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {pages.length > 0 && (
        <div className="bg-black/60 px-3 py-2 overflow-x-auto" data-testid="scanner-thumbs">
          <div className="flex gap-2 min-h-[4.5rem]">
            {pages.map((p, idx) => (
              <button
                key={p.id}
                onClick={() => setReviewIndex(idx)}
                data-testid={`scanner-thumb-${idx}`}
                className={`relative flex-shrink-0 w-14 h-18 rounded-md overflow-hidden border-2 ${
                  reviewIndex === idx ? 'border-teal-400' : 'border-white/20'
                }`}
                style={{ width: 54, height: 72 }}
              >
                <img src={p.dataUrl} alt="" className="w-full h-full object-cover" />
                <span className="absolute bottom-0.5 right-0.5 text-[10px] bg-black/60 text-white rounded px-1">
                  {idx + 1}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom action bar */}
      <div className="bg-black/80 px-4 py-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}>
        {reviewing ? (
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => retakePage(reviewing.id)}
              data-testid="scanner-retake"
              className="flex-1 max-w-xs px-4 py-3 rounded-full bg-white/10 text-white font-medium flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Retake
            </button>
            <button
              onClick={() => deletePage(reviewing.id)}
              data-testid="scanner-delete"
              className="flex-1 max-w-xs px-4 py-3 rounded-full bg-red-500/90 text-white font-medium flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button
              onClick={() => setReviewIndex(null)}
              data-testid="scanner-back-to-live"
              className="flex-1 max-w-xs px-4 py-3 rounded-full bg-teal-500 text-white font-semibold flex items-center justify-center gap-2"
            >
              <Camera className="w-4 h-4" /> Add Page
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-6">
            <div className="w-12 text-center text-white text-xs">
              {pages.length > 0 && (
                <button
                  onClick={() => setReviewIndex(pages.length - 1)}
                  className="flex flex-col items-center gap-1 active:scale-95"
                  data-testid="scanner-review"
                >
                  <FileText className="w-6 h-6" />
                  <span>Review</span>
                </button>
              )}
            </div>
            <button
              onClick={capturePage}
              disabled={starting || !!error}
              data-testid="scanner-capture"
              className="w-20 h-20 rounded-full bg-white flex items-center justify-center border-4 border-white/30 active:scale-95 disabled:opacity-40"
              aria-label="Capture page"
            >
              <div className="w-16 h-16 rounded-full bg-white border-2 border-black/10" />
            </button>
            <div className="w-12 text-center">
              {pages.length > 0 && (
                <div className="text-white text-xs">
                  <div className="font-bold text-lg">{pages.length}</div>
                  <div className="opacity-70">page{pages.length === 1 ? '' : 's'}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraScanner;
